import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';

/**
 * Presentational ecommerce product card. Slot-based — no business logic, no
 * mutations, no data fetching. Domain `ProductCard` and feature widgets compose
 * this primitive with formatted price strings, image URLs, and action buttons.
 *
 * Slots:
 *   - media        : image / gallery preview
 *   - mediaOverlay : badges + wishlist button laid over the media (top-left/right)
 *   - title        : product title (1–2 lines, truncates by default)
 *   - meta         : brand / category / rating row
 *   - price        : price block (current + compare-at)
 *   - actions      : footer actions (add to cart, etc.)
 *   - href         : if set, the whole card surface becomes the link
 */
export interface ProductCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  media?: ReactNode;
  mediaOverlay?: ReactNode;
  title?: ReactNode;
  meta?: ReactNode;
  price?: ReactNode;
  actions?: ReactNode;
  href?: string;
  /** Visual orientation. `vertical` = stacked card (grid), `horizontal` = list row. */
  orientation?: 'vertical' | 'horizontal';
  /** Media aspect ratio for the vertical variant. */
  mediaAspect?: 'square' | 'video' | 'portrait';
}

const aspect = {
  square: 'aspect-square',
  video: 'aspect-video',
  portrait: 'aspect-[3/4]',
} as const;

export const ProductCard = forwardRef<HTMLDivElement, ProductCardProps>(function ProductCard(
  {
    className,
    media,
    mediaOverlay,
    title,
    meta,
    price,
    actions,
    href,
    orientation = 'vertical',
    mediaAspect = 'square',
    ...props
  },
  ref,
) {
  const isHorizontal = orientation === 'horizontal';

  return (
    <div
      ref={ref}
      className={cn(
        'bg-bg-canvas border-border-strong text-fg-primary transition-base ease-token hover:border-accent/50 focus-within:border-accent/50 group relative flex overflow-hidden rounded-lg border shadow-md hover:shadow-lg focus-within:shadow-lg',
        isHorizontal ? 'flex-row gap-4 p-3' : 'flex-col',
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          'bg-bg-subtle relative overflow-hidden',
          isHorizontal
            ? 'aspect-square w-32 shrink-0 rounded-md'
            : cn('w-full', aspect[mediaAspect]),
        )}
      >
        {media}
        {mediaOverlay && (
          <div className="pointer-events-none absolute inset-0 flex flex-col">
            <div className="pointer-events-auto flex items-start justify-between gap-2 p-2">
              {mediaOverlay}
            </div>
          </div>
        )}
      </div>

      <div className={cn('flex min-w-0 flex-1 flex-col gap-2', isHorizontal ? 'py-1' : 'p-3')}>
        {meta && <div className="text-fg-muted text-xs">{meta}</div>}
        {title && (
          <div className="text-fg-primary line-clamp-2 text-sm font-medium leading-snug">
            {href ? (
              <a href={href} className="focus-visible:outline-none">
                <span className="absolute inset-0" aria-hidden="true" />
                {title}
              </a>
            ) : (
              title
            )}
          </div>
        )}
        {price && <div className="mt-auto">{price}</div>}
        {actions && <div className="relative z-10 mt-2 flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
});

/**
 * Standard price block: current price, optional strikethrough compare-at price.
 * Accepts already-formatted strings — formatting belongs in the feature/widget
 * layer (locale-aware Money formatting).
 */
export interface ProductPriceProps extends HTMLAttributes<HTMLDivElement> {
  current: ReactNode;
  compareAt?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const priceSize = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
} as const;

export function ProductPrice({
  current,
  compareAt,
  size = 'md',
  className,
  ...props
}: ProductPriceProps) {
  return (
    <div className={cn('flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5', className)} {...props}>
      <span className={cn('text-fg-primary max-w-full font-semibold [overflow-wrap:anywhere]', priceSize[size])}>{current}</span>
      {compareAt && <span className="text-fg-muted max-w-full text-xs line-through [overflow-wrap:anywhere]">{compareAt}</span>}
    </div>
  );
}
